import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': there are no
// lights in this scene at all - the chrome comes entirely from a matcap
// texture (see below), and a wash cone would just paint a flat wrong-looking
// patch across a surface that is supposed to be reflecting the album, not
// the rig.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'chromeflux',
  family: 'object',
  energyLevel: 0.55,
  bpmMin: 90,
  bpmMax: 140,
  movement: 0.45,
  danceability: 0.70,
  tonality: 0.85,
  enabled: true
};

// ChromeFlux: a single liquid-chrome torus knot, turning and rippling on pure
// black. Every other visualizer in this library is additive light drawn on
// darkness - this one is the deliberate exception, an opaque solid object
// with real matter to it. Do not pull it toward the others' aesthetic.
//
// The knot is chosen specifically because it self-interlaces: unlike a plain
// torus or sphere, its silhouette is unrecognizable from one viewing angle to
// the next, which is what makes a single continuously-turning object worth
// looking at for a whole track instead of a few seconds.
//
// WHICH knot is drawn fresh on every init - see KNOT_VARIANTS. The (p,q)
// winding pair, the strand thickness and the starting pose all re-roll, so
// two appearances of this visualizer never show the same object twice in a
// row. The ENVELOPE does not change with (p,q) - THREE's torus-knot curve
// always lives on the same torus, whose extent is set by the radius alone -
// so the frame-overflow requirement below holds for every variant without
// touching OBJECT_SCALE.
//
// THE MATCAP IS GENERATED FROM THE COVER, ONCE PER TRACK. This is the whole
// point of the piece: the "material" - not just a tint - changes with the
// album. See _buildMatcapTexture(). Nothing about this canvas draw happens
// inside update() or any per-frame path - see criterion 5 in the brief.
//
// PERFORMANCE SHAPE (fill-rate AND vertex-shader bound - this is the one
// visualizer in the library where vertex cost actually matters):
//  - ONE draw call for the knot, ONE more for its halo shell at tier 2 only.
//  - ZERO per-frame attribute uploads or geometry rebuilds. The liquid
//    displacement and its matching normal correction both run entirely in
//    the vertex shader from uTime/uAmp; update() writes four scalars.
//  - ONE texture read per fragment in steady state; the crossfade shader path
//    only ever runs during the 0.5s window right after a track changes.
//  - mediump fragment precision. Vertex precision is highp, matching every
//    other file in this codebase - the finite-difference normal correction
//    subtracts two very close sin() evaluations, and mediump's rounding
//    would visibly stair-step exactly the ripples this whole effect exists
//    to render.

const OBJECT_POS = [0, 3.4, 0];
const CAMERA_POS = [0, 3.4, 7.2];
const CAMERA_LOOK = [0, 3.4, 0];

const KNOT_RADIUS = 2.6;

// The (p,q) winding pairs the shape is drawn from, one draw per init.
// Curated, not random integers: p and q must be coprime (a common factor
// degenerates the knot into a simpler loop traced several times), and each
// pair carries its own strand thickness and tubular-segment multiplier -
// the denser the winding, the thinner the strand has to be for the
// interlace to stay readable rather than fusing into a lumpy torus, and
// the longer its path, the more segments it needs to stay smooth. The
// multiplier is bounded (1.5x worst case) because this file is the one
// vertex-bound visualizer in the library - see the performance comment
// above.
const KNOT_VARIANTS = [
  { p: 2, q: 3, tube: 0.62, tubularMul: 1.0 },  // trefoil - the original shape
  { p: 3, q: 2, tube: 0.58, tubularMul: 1.0 },  // three fat lobes
  { p: 2, q: 5, tube: 0.50, tubularMul: 1.25 }, // cinquefoil
  { p: 5, q: 2, tube: 0.44, tubularMul: 1.2 },  // five-pointed weave
  { p: 3, q: 4, tube: 0.46, tubularMul: 1.3 },
  { p: 4, q: 3, tube: 0.42, tubularMul: 1.3 },
  { p: 3, q: 5, tube: 0.40, tubularMul: 1.45 },
  { p: 2, q: 7, tube: 0.40, tubularMul: 1.5 }   // tight ribbon spiral
];
// Module-level on purpose: a random draw alone repeats itself one time in
// eight, and a repeat reads as "the shape did not change" - which is the
// one thing this mechanism exists to prevent. Tracking the last pick
// across instances guarantees consecutive appearances differ.
let _lastVariantIndex = -1;
function drawKnotVariant() {
  let idx = Math.floor(Math.random() * KNOT_VARIANTS.length);
  if (idx === _lastVariantIndex) idx = (idx + 1) % KNOT_VARIANTS.length;
  _lastVariantIndex = idx;
  return KNOT_VARIANTS[idx];
}
// A uniform scale on the OBJECT, not a change to the geometry's own
// parameters above (which stay exactly as specified). At 1x the knot's
// bounding sphere (radius 4.77) clears the frame's half-height at this
// camera (4.86) by only 2% - in practice that means almost no orientation in
// the rotation cycle actually pokes past the frame, which fails the brief's
// own "must overflow top and sides" requirement outright.
const OBJECT_SCALE = 1.35;

const TIER_LAYOUT = {
  // Tier 0 also drops the energy-driven second displacement frequency
  // entirely (not just zeroes its uniform) - three extra sin() evaluations
  // per vertex is real ALU cost on VideoCore VI, and the tier system exists
  // specifically to shed that on weak hardware.
  0: { tubular: 110, radial: 16, halo: false, extraFreq: false },
  1: { tubular: 160, radial: 24, halo: false, extraFreq: true },
  2: { tubular: 220, radial: 32, halo: true, extraFreq: true }
};
const DEFAULT_TIER = 2;

// --- Matcap ------------------------------------------------------------
const MATCAP_SIZE = 256;
const MATCAP_SPEC_RADIUS_FRAC = 0.18;
const MATCAP_SPEC_PEAK = 0.94; // luminance ceiling - bloom catches it, nothing clips to paper white
const MATCAP_FADE_SECONDS = 0.5;
const FALLBACK_VIVID = [0x8fd6ff, 0x3a4fc4, 0xff5fd8, 0xffe37a, 0x6dffd0];

// --- Liquid displacement -------------------------------------------------
// Three incommensurable-looking frequencies/rates on each axis, so the wave
// pattern never settles into a visible repeat within a track's length.
const DISP_FX = 1.9, DISP_FY = 2.3, DISP_FZ = 1.6;
const DISP_TX = 1.7, DISP_TY = 1.1, DISP_TZ = 0.8;
const DISP_FX2 = 3.7, DISP_FY2 = 4.1, DISP_FZ2 = 3.3;
const DISP_TX2 = 2.3, DISP_TY2 = 1.9, DISP_TZ2 = 1.4;
const FD_EPSILON = 0.05;
const AMP_MIN = 0.04, AMP_MAX = 0.16;
const BEAT_PULSE_AMOUNT = 0.55, BEAT_PULSE_DECAY = 11;
const ENERGY_THRESHOLD = 0.6; // normalized (0..1); no effect at all below this

// --- Rotation ------------------------------------------------------------
const BEATS_PER_REVOLUTION = 24;
const REFERENCE_BPM = 120; // no other file in this library assumes silence when bpm is missing
// The golden ratio: the real-number hardest to approximate by a rational, so
// pairing the two spin rates by this factor is the strongest available
// guarantee that the combined pose never revisits itself.
const GOLDEN_RATIO = (1 + Math.sqrt(5)) / 2;

// --- Halo shell ------------------------------------------------------------
// Fresnel exponent (3) is hand-written as t*t*t directly in the shader below
// rather than kept as a constant here - it is never anything but a literal
// GLSL multiply chain, so a named constant would just be one more thing to
// keep in sync with no benefit.
const HALO_SCALE = 1.03;
const HALO_INTENSITY = 0.4;

const PEAK_LUMINANCE = 0.92;

// artwork-palette.js's colours are tagged with a leading 1-bit
// ((1<<24)|rgb, see its own toHex()) purely so toString(16) always comes out
// a fixed width - every other file in this codebase never notices because
// THREE.Color.setHex() only ever reads bits 0-23. A raw toString(16) here
// does not get that masking for free and produces a 7-digit string that
// CanvasGradient.addColorStop rejects outright.
function hex(c) {
  return '#' + (c & 0xffffff).toString(16).padStart(6, '0');
}

// Scaling a colour by one factor keeps its hue; clamping per channel
// collapses a saturated colour toward white as soon as two channels hit the
// ceiling. Same convention as every other file in this codebase.
const CLAMP_CHUNK = `
  vec3 clampLuminance(vec3 col) {
    float mx = max(col.r, max(col.g, col.b));
    return mx > ${PEAK_LUMINANCE} ? col * (${PEAK_LUMINANCE} / mx) : col;
  }
`;

// Shared by the knot's own vertex shader and the halo shell's: the liquid
// offset and the finite-difference normal correction that makes it actually
// visible on a matcap surface (see the file-level comment on why this
// matters). extraFreq is spliced in only for tiers that budget for it.
function buildDisplacementChunk(extraFreq) {
  const extraTerm = extraFreq
    ? `
    if (uEnergyExtra > 0.0001) {
      float ex = sin(p.x * ${DISP_FX2} - uTime * ${DISP_TX2})
               * sin(p.y * ${DISP_FY2} + uTime * ${DISP_TY2})
               * sin(p.z * ${DISP_FZ2} - uTime * ${DISP_TZ2});
      d += ex * uEnergyExtra * 0.6;
    }
    `
    : '';
  return `
    uniform float uTime;
    uniform float uAmp;
    ${extraFreq ? 'uniform float uEnergyExtra;' : ''}

    float disp(vec3 p) {
      float d = sin(p.x * ${DISP_FX} + uTime * ${DISP_TX})
              * sin(p.y * ${DISP_FY} - uTime * ${DISP_TY})
              * sin(p.z * ${DISP_FZ} + uTime * ${DISP_TZ});
      ${extraTerm}
      return d;
    }

    // A matcap depends ONLY on the normal. Displacing vertices without
    // recomputing normals leaves the silhouette rippling while the surface
    // itself stays glassy-smooth - the liquid effect would be entirely
    // invisible except at the silhouette edge. Finite differences along two
    // tangent directions give a cheap, good-enough correction: three disp()
    // evaluations per vertex, negligible next to the vertex count.
    vec3 displace(vec3 pos, vec3 n, out vec3 newNormal) {
      vec3 t1 = normalize(cross(n, abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
      vec3 t2 = cross(n, t1);
      float d0 = disp(pos);
      float d1 = disp(pos + t1 * ${FD_EPSILON});
      float d2 = disp(pos + t2 * ${FD_EPSILON});
      newNormal = normalize(n - (t1 * (d1 - d0) + t2 * (d2 - d0)) * (uAmp / ${FD_EPSILON}));
      return pos + n * d0 * uAmp;
    }
  `;
}

function buildKnotVertexShader(extraFreq) {
  return `
    precision highp float;

    varying vec3 vNormalView;
    varying float vSafeAtt;

    ${buildDisplacementChunk(extraFreq)}
    ${SAFE_ZONE_VERTEX_CHUNK}

    void main() {
      vec3 newNormal;
      vec3 displaced = displace(position, normal, newNormal);

      vNormalView = normalize(normalMatrix * newNormal);
      vec4 clip = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
      gl_Position = clip;
      vSafeAtt = safeZoneAttenuation(clip);
    }
  `;
}

const knotFragmentShader = `
  precision mediump float;

  uniform sampler2D uMatcapA;
  uniform sampler2D uMatcapB;
  uniform float uMatcapMix;

  varying vec3 vNormalView;
  varying float vSafeAtt;

  ${CLAMP_CHUNK}

  void main() {
    vec3 n = normalize(vNormalView);
    vec2 muv = n.xy * 0.5 + 0.5;

    // One texture read in steady state. uMatcapMix sits at 0 outside the
    // 0.5s post-track-change crossfade, so the second sample and the mix are
    // skipped entirely the vast majority of the time - a uniform-condition
    // branch like this costs nothing on the GPUs this targets, unlike a
    // per-fragment data-dependent branch.
    vec3 col = texture2D(uMatcapA, muv).rgb;
    if (uMatcapMix > 0.001) {
      vec3 colB = texture2D(uMatcapB, muv).rgb;
      col = mix(col, colB, uMatcapMix);
    }

    col *= vSafeAtt;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// tube: the strand radius of THIS init's knot variant (see KNOT_VARIANTS) -
// baked as a literal because the shader is only ever built once per init,
// after the variant is drawn, and a uniform would be one more per-frame
// slot for a value that never changes within an instance's lifetime.
function buildHaloVertexShader(extraFreq, tube) {
  return `
    precision highp float;

    varying vec3 vNormalView;
    varying vec3 vViewDir;
    varying float vSafeAtt;

    ${buildDisplacementChunk(extraFreq)}
    ${SAFE_ZONE_VERTEX_CHUNK}

    void main() {
      vec3 newNormal;
      // Scaled 1.03x AFTER displacement, along the (already corrected)
      // normal - the shell tracks the actual liquid surface at every frame
      // rather than a static undeformed offset that would visibly detach
      // from the knot mid-ripple.
      vec3 displaced = displace(position, normal, newNormal) + newNormal * ${(HALO_SCALE - 1).toFixed(4)} * ${tube.toFixed(2)};

      vNormalView = normalize(normalMatrix * newNormal);
      vec4 worldPos = modelMatrix * vec4(displaced, 1.0);
      vViewDir = normalize(cameraPosition - worldPos.xyz);

      vec4 clip = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
      gl_Position = clip;
      vSafeAtt = safeZoneAttenuation(clip);
    }
  `;
}

const haloFragmentShader = `
  precision mediump float;

  uniform vec3 uHaloColor;

  varying vec3 vNormalView;
  varying vec3 vViewDir;
  varying float vSafeAtt;

  ${CLAMP_CHUNK}

  void main() {
    vec3 n = normalize(vNormalView);
    float t = 1.0 - abs(dot(n, normalize(vViewDir)));
    float f = t * t * t; // pow(t, 3.0) written as multiplies, matching this file's own convention

    vec3 col = uHaloColor * f * ${HALO_INTENSITY.toFixed(2)} * vSafeAtt;
    // Additive blending is (SRC_ALPHA, ONE): all intensity lives in rgb,
    // alpha stays 1.
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

function progressAmplitude(profile) {
  if (profile.duration <= 0) return (AMP_MIN + AMP_MAX) / 2;
  const t = THREE.MathUtils.clamp(profile.progress, 0, 1);
  return AMP_MIN + (AMP_MAX - AMP_MIN) * t;
}

// Builds the matcap: a vertical gradient standing in for the ground/sky a
// real chrome surface reflects, two or three sharp horizontal "environment"
// bands in the album's other vivid colours, a capped-luminance specular
// highlight, and a radial darkening toward the disc edge with hard black
// outside it (a matcap is only ever sampled inside the unit circle inscribed
// in the square texture, but bilinear filtering right at that edge can still
// pull in a neighbouring texel - black corners make that free rather than a
// visible seam on grazing silhouettes).
function buildMatcapTexture(palette) {
  const vivid = (palette && palette.vividColors) || [];
  const source = vivid.length >= 2 ? vivid : FALLBACK_VIVID;

  const tmp = new THREE.Color();
  const hsl = { h: 0, s: 0, l: 0 };
  const lightnessOf = (h) => { tmp.set(h); tmp.getHSL(hsl); return hsl.l; };

  const g0 = source[0], g1 = source[1];
  const top = lightnessOf(g0) >= lightnessOf(g1) ? g0 : g1;
  const bottom = top === g0 ? g1 : g0;

  const bandSource = source.filter((c) => c !== top && c !== bottom);
  const bandPool = bandSource.length > 0 ? bandSource : source;
  const bandCount = bandPool.length >= 3 ? 3 : 2;
  const bandColors = [];
  for (let i = 0; i < bandCount; i++) bandColors.push(bandPool[i % bandPool.length]);

  const canvas = document.createElement('canvas');
  canvas.width = MATCAP_SIZE;
  canvas.height = MATCAP_SIZE;
  const ctx = canvas.getContext('2d');
  const N = MATCAP_SIZE;

  const sky = ctx.createLinearGradient(0, 0, 0, N);
  sky.addColorStop(0, hex(top));
  sky.addColorStop(1, hex(bottom));
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, N, N);

  // Fixed, hand-placed bands rather than randomised ones - three tasteful
  // "horizon lines" read as environment reflections; scattered random ones
  // just read as noise.
  const bandLayout = bandCount === 3
    ? [{ y: 0.30, h: 0.09 }, { y: 0.52, h: 0.07 }, { y: 0.74, h: 0.11 }]
    : [{ y: 0.38, h: 0.10 }, { y: 0.66, h: 0.09 }];
  for (let i = 0; i < bandLayout.length; i++) {
    ctx.fillStyle = hex(bandColors[i]);
    ctx.fillRect(0, N * bandLayout[i].y, N, N * bandLayout[i].h);
  }

  // Specular tache, top-left, luminance capped so bloom catches it without
  // ever clipping to paper white.
  const specPeak = Math.round(255 * MATCAP_SPEC_PEAK);
  const specX = N * 0.32, specY = N * 0.28, specR = N * MATCAP_SPEC_RADIUS_FRAC;
  const spec = ctx.createRadialGradient(specX, specY, 0, specX, specY, specR);
  spec.addColorStop(0, `rgba(${specPeak}, ${specPeak}, ${specPeak}, 1)`);
  spec.addColorStop(1, `rgba(${specPeak}, ${specPeak}, ${specPeak}, 0)`);
  ctx.fillStyle = spec;
  ctx.fillRect(0, 0, N, N);

  // Smooth darkening toward the disc's edge - a real chrome sphere's matcap
  // is never uniformly lit all the way to the rim.
  const cx = N / 2, cy = N / 2, r = N / 2;
  const vignette = ctx.createRadialGradient(cx, cy, r * 0.55, cx, cy, r);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.85)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, N, N);

  // Hard black outside the unit circle - see the function comment.
  const img = ctx.getImageData(0, 0, N, N);
  const d = img.data;
  const r2 = r * r;
  for (let y = 0; y < N; y++) {
    const dy = y - cy;
    for (let x = 0; x < N; x++) {
      const dx = x - cx;
      if (dx * dx + dy * dy > r2) {
        const i = (y * N + x) * 4;
        d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

export class ChromeFluxVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    const layout = TIER_LAYOUT[this._tier];
    this._layout = layout;

    const variant = drawKnotVariant();
    this._variant = variant;
    const geometry = new THREE.TorusKnotGeometry(
      KNOT_RADIUS, variant.tube,
      Math.round(layout.tubular * variant.tubularMul), layout.radial,
      variant.p, variant.q
    );
    this._geometry = geometry;

    const fallbackMatcap = buildMatcapTexture(null);
    this._matcapA = fallbackMatcap;
    this._matcapB = null;

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uAmp: { value: AMP_MIN },
      uMatcapA: { value: this._matcapA },
      uMatcapB: { value: this._matcapA },
      uMatcapMix: { value: 0 }
    });
    if (layout.extraFreq) this._uniforms.uEnergyExtra = { value: 0 };

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: buildKnotVertexShader(layout.extraFreq),
      fragmentShader: knotFragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true
    });
    this._mesh = new THREE.Mesh(geometry, this._material);
    this._mesh.position.set(OBJECT_POS[0], OBJECT_POS[1], OBJECT_POS[2]);
    this._mesh.scale.setScalar(OBJECT_SCALE);
    scene.add(this._mesh);

    if (layout.halo) {
      this._haloUniforms = Object.assign({}, safeZoneUniforms, {
        uTime: this._uniforms.uTime,
        uAmp: this._uniforms.uAmp,
        uHaloColor: { value: new THREE.Color(FALLBACK_VIVID[0]) }
      });
      if (layout.extraFreq) this._haloUniforms.uEnergyExtra = this._uniforms.uEnergyExtra;
      this._haloMaterial = new THREE.ShaderMaterial({
        uniforms: this._haloUniforms,
        vertexShader: buildHaloVertexShader(layout.extraFreq, variant.tube),
        fragmentShader: haloFragmentShader,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        side: THREE.FrontSide,
        blending: THREE.AdditiveBlending
      });
      // Reuses the SAME geometry object - the halo is a second draw call on
      // identical vertex data, not a duplicated buffer. ~14k triangles x 2
      // draw calls matches the tier's own ~28k-triangle budget.
      this._halo = new THREE.Mesh(geometry, this._haloMaterial);
      this._halo.position.copy(this._mesh.position);
      this._halo.scale.copy(this._mesh.scale);
      scene.add(this._halo);
    }

    // Pose and liquid phase both start from a fresh random point, so even
    // the rare case of the same knot recurring after an intervening variant
    // opens on a different silhouette with a different ripple pattern -
    // "la forme change a chaque chargement" is about what is SEEN, and the
    // first seconds of a fixed pose were the most recognisable part.
    this._time = Math.random() * 100;
    this._angle1 = Math.random() * Math.PI * 2;
    this._angle2 = Math.random() * Math.PI * 2;
    this._lastPaletteRef = null;
    this._fading = false;
    this._fadeElapsed = 0;
  }

  // Swaps in a freshly generated matcap for the given palette, crossfading
  // from whatever is currently showing. If a fade is already running (a fast
  // track skip landed inside the previous 0.5s window), the in-flight target
  // is promoted to "current" immediately rather than letting a third texture
  // pile up.
  _swapMatcap(palette) {
    if (this._fading) {
      this._matcapA.dispose();
      this._matcapA = this._matcapB;
    }
    this._matcapB = buildMatcapTexture(palette);
    this._fading = true;
    this._fadeElapsed = 0;
    this._uniforms.uMatcapB.value = this._matcapB;
    this._uniforms.uMatcapMix.value = 0;

    const vivid = (palette && palette.vividColors) || [];
    if (this._haloUniforms) {
      this._haloUniforms.uHaloColor.value.set(vivid.length > 0 ? vivid[0] : FALLBACK_VIVID[0]);
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;
    this._uniforms.uTime.value = this._time;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._swapMatcap(profile.artworkPalette);
    }

    if (this._fading) {
      // Wall clock, not the freezable track time - a track-change fade
      // should not stall just because playback happened to be paused when
      // it started.
      this._fadeElapsed += deltaTime;
      const t = Math.min(1, this._fadeElapsed / MATCAP_FADE_SECONDS);
      this._uniforms.uMatcapMix.value = t;
      if (t >= 1) {
        this._matcapA.dispose();
        this._matcapA = this._matcapB;
        this._matcapB = null;
        this._uniforms.uMatcapA.value = this._matcapA;
        this._uniforms.uMatcapMix.value = 0;
        this._fading = false;
      }
    }

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || REFERENCE_BPM;
    if (!paused) {
      // One full revolution of the primary axis every BEATS_PER_REVOLUTION
      // beats; the secondary axis turns at the golden-ratio multiple of that
      // rate, so the combined pose never repeats within a track's length.
      // Angles (not just a clock) accumulate, so freezing on pause holds the
      // exact pose rather than snapping.
      const revPerSecond = bpm / (60 * BEATS_PER_REVOLUTION);
      const rate1 = 2 * Math.PI * revPerSecond;
      this._angle1 += deltaTime * rate1;
      this._angle2 += deltaTime * rate1 * GOLDEN_RATIO;
    }
    this._mesh.rotation.set(this._angle2, this._angle1, 0);
    if (this._halo) this._halo.rotation.copy(this._mesh.rotation);

    const baseAmp = progressAmplitude(profile);
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._uniforms.uAmp.value = baseAmp * pulse;

    if (this._layout.extraFreq) {
      const energy = profile.trackFeatures && profile.trackFeatures.energy;
      const energyNorm = energy != null ? energy / 100 : null;
      this._uniforms.uEnergyExtra.value = energyNorm != null
        ? THREE.MathUtils.clamp((energyNorm - ENERGY_THRESHOLD) / (1 - ENERGY_THRESHOLD), 0, 1)
        : 0;
    }
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._geometry.dispose();
    this._material.dispose();
    if (this._halo) {
      this._scene.remove(this._halo);
      // Geometry is owned by (and disposed with) _mesh above - the halo only
      // ever borrowed it.
      this._haloMaterial.dispose();
    }
    this._matcapA.dispose();
    if (this._matcapB) this._matcapB.dispose();
  }
}
